import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  TextField,
  Button,
  Rating,
  useTheme,
  IconButton,
  Divider,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useState } from "react";
import { FormProvider, RHFTextField } from "../hook-form";
import { useForm } from "react-hook-form";
import { Strings } from "@app/constants";
import { useResponsive } from "@app/hooks";
import { useDispatch } from "react-redux";
import { addReview } from "@app/modules/product-detail/slice";

interface ReviewModalProps {
  open: boolean;
  onClose: () => void;
  productId?: any;
}

const ReviewModal = ({ open, onClose, productId }: ReviewModalProps) => {
  const theme = useTheme();
  const [rating, setRating] = useState<number | null>(0);
  const isMobile = useResponsive("down", "md");
  const dispatch = useDispatch();

  const methods = useForm();

  const handleClose = () => {
    setRating(0);
    methods.reset();
    onClose();
  };

  const onSubmit = (form: any) => {
    dispatch(addReview({ ...form, rating, carId: productId }));
    methods.reset();
    setRating(0);
    handleClose();
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          mx: isMobile ? 0 : "auto",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: isMobile ? 2 : 3,
          py: isMobile ? 1.5 : 2,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            fontWeight="bold"
            sx={{
              color: "#1A202C",
              fontSize: isMobile ? "18px" : "20px",
            }}
          >
            Add Reviews
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Icon icon="mdi:close" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider sx={{ width: "100%", mx: 0, my: 2 }} />

      <FormProvider methods={methods}>
        <DialogContent
          sx={{
            px: isMobile ? 2 : 3,
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
            "-ms-overflow-style": "none",
          }}
        >
          <Stack spacing={isMobile ? 2 : 3}>
            <Stack
              direction={isMobile ? "column" : "row"}
              alignItems={isMobile ? "flex-start" : "center"}
              spacing={1.5}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: isMobile ? "14px" : "16px",
                }}
              >
                Your review about this product:
              </Typography>

              <Rating
                value={rating}
                onChange={(_, newValue) => setRating(newValue)}
                size="small"
                sx={{
                  "& .MuiRating-icon": {
                    fontSize: isMobile ? "1.25rem" : "1.5rem",
                  },
                }}
              />
            </Stack>
            <Stack spacing={1}>
              <Typography variant="body2" fontWeight="500">
                Review*
              </Typography>
              <RHFTextField
                name="comment"
                placeholder="Write your review"
                multiline
                rows={isMobile ? 3 : 4}
                fullWidth
              />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: isMobile ? 2 : 3,
            pb: isMobile ? 2 : 3,
          }}
        >
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={2}
            justifyContent="flex-end"
            width="100%"
          >
            <Button
              onClick={handleClose}
              sx={{
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                textTransform: "none",
                fontWeight: 500,
                width: isMobile ? "100%" : "35%",
                py: 1.3,
              }}
              variant="outlined"
            >
              {Strings.common.close}
            </Button>

            <Button
              onClick={methods.handleSubmit(onSubmit)}
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 500,
                width: isMobile ? "100%" : "35%",
                py: 1.3,
                backgroundColor: theme.palette.primary.main,
              }}
            >
              Post Review
            </Button>
          </Stack>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};

export { ReviewModal };
